/**
 * Utility functions for permission checking in the frontend
 */

/**
 * Get the user's permissions from localStorage
 * @returns {Object} The user's permissions or null if not found
 */
export const getUserPermissions = () => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;
  
  try {
    const user = JSON.parse(userStr);
    return user.permissions || null;
  } catch (error) {
    console.error('Error parsing user data from localStorage:', error);
    return null;
  }
};

/**
 * Get the segment access level for the current user
 * Returns one of: 'view_only', 'create_delete_own', 'create_for_all', 'full_access'
 * @returns {string} The segment access level
 */
export const getSegmentAccessLevel = () => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return 'view_only';
  
  try {
    const user = JSON.parse(userStr);
    
    // Superuser has full access
    if (user.role === 'superuser') return 'full_access';
    
    // Check for segment access level in role_permissions
    if (user.permissions?.leads?.segments_access?.value) {
      return user.permissions.leads.segments_access.value;
    }
    
    // Default to view only
    return 'view_only';
  } catch (error) {
    console.error('Error checking segment access level:', error);
    return 'view_only';
  }
};

/**
 * Get calendar view access level
 * Returns one of: 'none', 'own', 'all'
 * @returns {string} The calendar view access level
 */
export const getCalendarViewAccess = () => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return 'none';

  try {
    const user = JSON.parse(userStr);

    console.log('User role:', user.role);
    console.log('Calendar permissions:', user.permissions?.calendar);

    // Superuser has all access
    if (user.role === 'superuser') {
      console.log('User is superuser, granting all access');
      return 'all';
    }

    // Now properly checking read.value='all', no need for role override

    // Check permissions
    if (!user.permissions?.calendar?.access?.enabled) {
      console.log('User has no calendar access permission');
      return 'none';
    }

    // Check read permission value
    if (user.permissions?.calendar?.read?.enabled) {
      // If read.value is set to 'all', grant all access
      if (user.permissions?.calendar?.read?.value === 'all') {
        console.log('User has read.value="all" permission for calendar -> all access');
        return 'all';
      }

      // Otherwise, grant own access
      console.log('User has read permission for calendar -> own access');
      return 'own';
    }

    console.log('User has no specific calendar view permissions');
    return 'none';
  } catch (error) {
    console.error('Error checking calendar view access:', error);
    return 'none';
  }
};

/**
 * Checks if a user can perform an action on a specific calendar event
 * @param {string} action - The action to check ('update', 'delete', 'complete')
 * @param {object} event - The calendar event object to check permission for
 * @returns {boolean} Whether the user can perform the action on this event
 */
export const canModifyCalendarEvent = (action, event) => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return false;
  
  try {
    const user = JSON.parse(userStr);
    
    // Superuser can do anything
    if (user.role === 'superuser') return true;
    
    // Must have basic permission first
    if (!user.permissions?.calendar?.[action]?.enabled) return false;

    // If user has ownOnly permission for this action, check if they own the event
    if (user.permissions?.calendar?.[`${action}OwnOnly`]?.enabled === true &&
        event.tutor_id !== user.id) {
      return false;
    }
    
    return true;
  } catch (error) {
    console.error(`Error checking calendar event ${action} permission:`, error);
    return false;
  }
};

/**
 * Checks if a user can perform an action on a specific employee
 * @param {string} action - The action to check ('update', 'delete')
 * @param {object} employee - The employee object to check permission for
 * @returns {boolean} Whether the user can perform the action on this employee
 */
export const canModifyEmployee = (action, employee) => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return false;
  
  try {
    const user = JSON.parse(userStr);
    
    // Superuser can do anything
    if (user.role === 'superuser') return true;
    
    // Must have basic permission first
    if (!user.permissions?.employees?.[action]?.enabled) return false;

    // If user has ownOnly permission for this action, check if they are the employee (self-management)
    if (user.permissions?.employees?.[`${action}OwnOnly`]?.enabled === true &&
        employee.id !== user.id) {
      return false;
    }
    
    return true;
  } catch (error) {
    console.error(`Error checking employee ${action} permission:`, error);
    return false;
  }
};

/**
 * Get leads view access level
 * Returns one of: 'none', 'own', 'all'
 * @returns {string} The leads view access level
 */
export const getLeadsViewAccess = () => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return 'none';
  
  try {
    const user = JSON.parse(userStr);
    
    // Superuser has all access
    if (user.role === 'superuser') return 'all';
    
    // Check permissions
    if (!user.permissions?.leads?.access?.enabled) return 'none';
    if (user.permissions?.leads?.viewAll?.enabled) return 'all';
    if (user.permissions?.leads?.read?.enabled) return 'own';
    
    return 'none';
  } catch (error) {
    console.error('Error checking leads view access:', error);
    return 'none';
  }
};

/**
 * Get deals view access level
 * Returns one of: 'none', 'own', 'all'
 * @returns {string} The deals view access level
 */
export const getDealsViewAccess = () => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return 'none';
  
  try {
    const user = JSON.parse(userStr);
    
    // Superuser has all access
    if (user.role === 'superuser') return 'all';
    
    // Check permissions
    if (!user.permissions?.deals?.access?.enabled) return 'none';
    if (user.permissions?.deals?.viewAll?.enabled) return 'all';
    if (user.permissions?.deals?.read?.enabled) return 'own';
    
    return 'none';
  } catch (error) {
    console.error('Error checking deals view access:', error);
    return 'none';
  }
};

/**
 * Get tasks view access level
 * Returns one of: 'none', 'own', 'all'
 * @returns {string} The tasks view access level
 */
export const getTasksViewAccess = () => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return 'none';
  
  try {
    const user = JSON.parse(userStr);
    
    // Superuser has all access
    if (user.role === 'superuser') return 'all';
    
    // Check permissions
    if (!user.permissions?.tasks?.access?.enabled) return 'none';
    if (user.permissions?.tasks?.viewAll?.enabled) return 'all';
    if (user.permissions?.tasks?.read?.enabled) return 'own';
    
    return 'none';
  } catch (error) {
    console.error('Error checking tasks view access:', error);
    return 'none';
  }
};

/**
 * Checks if a user can perform an action on a specific lead
 * @param {string} action - The action to check ('update', 'delete', 'export')
 * @param {object} lead - The lead object to check permission for
 * @returns {boolean} Whether the user can perform the action on this lead
 */
export const canModifyLead = (action, lead) => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return false;
  
  try {
    const user = JSON.parse(userStr);
    
    // Superuser can do anything
    if (user.role === 'superuser') return true;
    
    // Must have basic permission first
    if (!user.permissions?.leads?.[action]?.enabled) return false;

    // If user has ownOnly permission for this action, check if they own the lead
    if (user.permissions?.leads?.[`${action}OwnOnly`]?.enabled === true &&
        lead.assigned_to !== user.id) {
      return false;
    }
    
    return true;
  } catch (error) {
    console.error(`Error checking lead ${action} permission:`, error);
    return false;
  }
};

/**
 * Checks if a user can perform an action on a specific deal
 * @param {string} action - The action to check ('update', 'delete', 'export')
 * @param {object} deal - The deal object to check permission for
 * @returns {boolean} Whether the user can perform the action on this deal
 */
export const canModifyDeal = (action, deal) => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return false;
  
  try {
    const user = JSON.parse(userStr);
    
    // Superuser can do anything
    if (user.role === 'superuser') return true;
    
    // Must have basic permission first
    if (!user.permissions?.deals?.[action]?.enabled) return false;
    
    // If user has ownOnly permission for this action, check if they own the deal
    if (user.permissions?.deals?.[`${action}OwnOnly`]?.enabled === true &&
        deal.deal_owner_id !== user.id) {
      return false;
    }
    
    return true;
  } catch (error) {
    console.error(`Error checking deal ${action} permission:`, error);
    return false;
  }
};

/**
 * Checks if a user can perform an action on a specific task
 * @param {string} action - The action to check ('update', 'delete')
 * @param {object} task - The task object to check permission for
 * @returns {boolean} Whether the user can perform the action on this task
 */
export const canModifyTask = (action, task) => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return false;
  
  try {
    const user = JSON.parse(userStr);
    
    // Superuser can do anything
    if (user.role === 'superuser') return true;
    
    // Must have basic permission first
    if (!user.permissions?.tasks?.[action]?.enabled) return false;

    // If user has ownOnly permission for this action, check if they own the task
    if (user.permissions?.tasks?.[`${action}OwnOnly`]?.enabled === true &&
        task.assigned_to !== user.id) {
      return false;
    }
    
    return true;
  } catch (error) {
    console.error(`Error checking task ${action} permission:`, error);
    return false;
  }
};

/**
 * Check if the current user has a specific permission
 * @param {string} category - The permission category (e.g., 'leads', 'deals', 'calendar')
 * @param {string} permission - The specific permission (e.g., 'create', 'read', 'update', 'delete')
 * @returns {boolean} True if the user has the permission, false otherwise
 */
export const hasPermission = (category, permission) => {
  // Get user data from localStorage
  const userStr = localStorage.getItem('user');
  if (!userStr) return false;
  
  try {
    const user = JSON.parse(userStr);
    
    // Special case: platform admin check
    if (category === 'platform' && permission === 'admin') {
      return user.id === 1;
    }
    
    // Superuser has all permissions
    if (user.role === 'superuser') return true;
    
    // Check if permissions exist in user data
    if (!user.permissions) {
      // Fall back to legacy role-based permissions
      return hasLegacyRolePermission(user.role, category, permission);
    }
    
    // Check if the category exists in the permissions object
    if (!user.permissions[category]) return false; // Category check is fine

    // Check if the permission key exists within the category and has 'enabled' property set to true.
    // Use optional chaining (?.) for safety.
    const permissionValue = user.permissions[category]?.[permission];
    return permissionValue?.enabled === true;
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
};

/**
 * Legacy role-based permission checking (for backward compatibility)
 * @param {string} role - The user's role
 * @param {string} category - The permission category
 * @param {string} permission - The specific permission
 * @returns {boolean} True if the role has the permission, false otherwise
 */
const hasLegacyRolePermission = (role, category, permission) => {
  // Superuser has all permissions
  if (role === 'superuser') return true;
  
  // Define role-based permissions
  const rolePermissions = {
    admin: {
      leads: ['access', 'create', 'read', 'update', 'delete', 'assign', 'export'], // Added 'access'
      deals: ['access', 'create', 'read', 'update', 'delete', 'assign', 'export'], // Added 'access'
      calendar: ['access', 'create', 'read', 'update', 'delete', 'viewAll'],       // Added 'access'
      employees: ['access', 'create', 'read', 'update', 'viewAll'],                // Added 'access'
      tasks: ['access', 'create', 'read', 'update', 'delete', 'assignAll'],       // Added 'access'
      settings: ['access', 'view', 'update'],                                      // Added 'access'
      kpis: ['access'],                                                            // Added category + access
      mailer: ['access'],                                                          // Added category + access
      chat: ['access'],                                                            // Added category + access
      admin: ['manageRoles', 'viewLogs'] // manageUsers removed (consolidated into employees)
    },
    sales: {
      leads: ['access', 'create', 'read', 'update'], // Added 'access'
      deals: ['create', 'read', 'update'],
      deals: ['access', 'create', 'read', 'update'], // Added 'access'
      calendar: ['access', 'create', 'read', 'update', 'delete'], // Added 'access'
      employees: ['access', 'read'], // Added 'access'
      tasks: ['access', 'create', 'read', 'update'], // Added 'access'
      chat: ['access'] // Added category + access
    },
    tutor: {
      calendar: ['access', 'read', 'update'], // Added 'access'
      employees: ['access', 'read'], // Added 'access'
      tasks: ['access', 'create', 'read', 'update'], // Added 'access'
      chat: ['access'] // Added category + access
    }
  };
  
  // Check if the role has the permission
  if (!rolePermissions[role]) return false;
  if (!rolePermissions[role][category]) return false;
  return rolePermissions[role][category].includes(permission);
};

/**
 * Check if the current user has any of the specified permissions
 * @param {Array} permissions - Array of [category, permission] pairs
 * @returns {boolean} True if the user has any of the permissions, false otherwise
 */
export const hasAnyPermission = (permissions) => {
  return permissions.some(([category, permission]) => hasPermission(category, permission));
};

/**
 * Check if the current user has all of the specified permissions
 * @param {Array} permissions - Array of [category, permission] pairs
 * @returns {boolean} True if the user has all of the permissions, false otherwise
 */
export const hasAllPermissions = (permissions) => {
  return permissions.every(([category, permission]) => hasPermission(category, permission));
};

/**
 * Check if user has permission to perform segment operations
 * @param {string} action - One of 'view', 'create_own', 'create_public', 'delete_own', 'delete_public'
 * @param {object} segment - Optional segment object to check ownership
 * @returns {boolean} Whether the user can perform this action
 */
export const canPerformSegmentAction = (action, segment = null) => {
  const accessLevel = getSegmentAccessLevel();
  const userStr = localStorage.getItem('user');
  if (!userStr) return false;
  
  const user = JSON.parse(userStr);
  
  // Superuser can do anything
  if (user.role === 'superuser') return true;
  
  // Check if user has basic leads access
  if (!hasPermission('leads', 'access')) return false;
  
  // Permission levels mapped to numeric values
  const levelValues = {
    'view_only': 0,
    'create_delete_own': 1,
    'create_for_all': 2,
    'full_access': 3
  };
  
  switch (action) {
    case 'view':
      // All levels can view segments
      return true;
      
    case 'create_own':
      // Levels 1+ can create own segments
      return levelValues[accessLevel] >= 1;
      
    case 'create_public':
      // Levels 2+ can create public segments
      return levelValues[accessLevel] >= 2;
      
    case 'delete_own':
      // Need level 1+ to delete own segments
      return levelValues[accessLevel] >= 1;
      
    case 'delete_any':
      // Only full access can delete any segment
      return levelValues[accessLevel] === 3;
      
    case 'delete':
      // For delete with a segment object, check ownership
      if (!segment) return false;
      if (levelValues[accessLevel] === 3) return true; // Full access can delete any
      if (levelValues[accessLevel] >= 1 && segment.user_id === user.id) return true; // Can delete own
      return false;
      
    default:
      return false;
  }
};

/**
 * Check if a user has lead access based on their permissions
 * @param {object} user - The user object to check
 * @returns {boolean} Whether the user has lead access
 */
export const hasLeadAccess = (user) => {
  // Check if this is a valid user object
  if (!user) return false;

  // Superuser always has access
  if (user.role === 'superuser') return true;

  try {
    // Direct permission check: Does the user have explicit lead access?
    if (user.permissions?.leads?.access?.enabled) return true;

    // Check if the user has ANY lead-related permissions that would indicate lead access
    if (user.permissions?.leads) {
      // Check for common lead permissions that would indicate access
      const leadPermissions = ['create', 'read', 'update', 'delete', 'assign', 'export', 'viewAll'];
      for (const perm of leadPermissions) {
        if (user.permissions.leads[perm]?.enabled === true) {
          return true;
        }
      }
    }

    // Fallback to legacy role-based permissions
    return hasLegacyRolePermission(user.role, 'leads', 'access');
  } catch (error) {
    console.error('Error in hasLeadAccess:', error);
    // If there's an error, default to false for safety
    return false;
  }
};

// Cross-tab storage keys for real-time updates
export const getCrossTabStorageKeys = () => {
  return {
    LEAD_UPDATED: 'edusuite_lead_updated',
    LEAD_CREATED: 'edusuite_lead_created',
    LEAD_DELETED: 'edusuite_lead_deleted',
  };
};
